/**
 * Represents a single FAQ entry
 * @typedef {Object} FAQItem
 * @property {string} question - FAQ question text
 * @property {string} answer   - FAQ answer text
 */

/**
 * Represents a category/group of FAQs
 * Supports multiple backend response shapes
 *
 * @typedef {Object} FAQCategory
 * @property {FAQItem[]=} details - FAQ list under "details" key
 * @property {FAQItem[]=} faqList - Alternate FAQ list key
 */

/**
 * Props for FAQChatBotMetaData component
 *
 * @typedef {Object} FAQChatBotMetaDataProps
 * @property {FAQCategory[]} helpContent - Full FAQ dataset grouped by category
 * @property {string=} language          - Selected language (defaults to 'en')
 */

import React from 'react';
import PropTypes from 'prop-types';
import style from './KidsChatBotFAQ.module.css';

/**
 * FAQChatBotMetaData
 * ----------------------------------------
 * Displays high-level metadata about the FAQ dataset:
 * - Total number of categories
 * - Total number of FAQ items across all categories
 * - Timestamp when the data is rendered
 * - Selected language
 * - Data loading status
 *
 * This component is read-only and does not mutate data.
 *
 * @param {FAQChatBotMetaDataProps} props
 */
function FAQChatBotMetaData({ helpContent, language }) {
  /**
   * Calculate total number of FAQ items across all categories.
   * Handles multiple possible keys (`details` or `faqList`)
   * and guards against invalid or unexpected data shapes.
   *
   * @type {number}
   */
  const totalItems = helpContent.reduce((acc, cat) => {
    // Normalize FAQ list regardless of backend response shape
    const faqItems = cat?.details || cat?.faqList || [];

    // Defensive check to ensure array before accessing length
    return acc + (Array.isArray(faqItems) ? faqItems.length : 0);
  }, 0);

  return (
    <div className={style.metadata}>
      <p>
        <strong>Total Categories:</strong> {helpContent.length}
      </p>

      <p>
        <strong>Total Items:</strong> {totalItems}
      </p>

      <p>
        <strong>Generated:</strong> {new Date().toISOString()}
      </p>

      <p>
        <strong>Language:</strong> {language || 'en'}
      </p>

      <p>
        <strong>Data Status:</strong> Loaded
      </p>
    </div>
  );
}

/**
 * Runtime prop validation
 */
FAQChatBotMetaData.propTypes = {
  helpContent: PropTypes.arrayOf(
    PropTypes.shape({
      details: PropTypes.arrayOf(
        PropTypes.shape({
          question: PropTypes.string.isRequired,
          answer: PropTypes.string.isRequired
        })
      ),
      faqList: PropTypes.arrayOf(
        PropTypes.shape({
          question: PropTypes.string.isRequired,
          answer: PropTypes.string.isRequired
        })
      )
    })
  ).isRequired,

  language: PropTypes.string
};

/**
 * Default props for optional values
 */
FAQChatBotMetaData.defaultProps = {
  language: 'en'
};

export default FAQChatBotMetaData;
